import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Table,
  Tag,
  Input,
  Select,
  Button,
  Space,
  notification,
  Tooltip,
  Statistic,
  Row,
  Col,
  Card,
  Avatar,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  LogoutOutlined,
  EyeOutlined,
  UserAddOutlined,
  UserOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { HITECH_API, getAuthHeaders } from "./api";
import ApplicationDetail from "./ApplicationDetail";
import CreateUser from "./CreateUser";
import ExportDrawer from "./ExportDrawer";

const STATUS_COLORS = {
  pending: "orange",
  shortlisted: "green",
  rejected: "red",
};
const DEFAULT_PAGE_SIZE = 15;

const AdminDashboard = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [departments, setDepartments] = useState([]);

  // Server-side filter/pagination state
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deptFilter, setDeptFilter] = useState("all");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
  });
  const [counts, setCounts] = useState({
    total: 0,
    pending: 0,
    shortlisted: 0,
    rejected: 0,
  });

  const searchDebounce = useRef(null);
  const navigate = useNavigate();
  const adminUser = JSON.parse(
    localStorage.getItem("hitech_admin_user") || "{}",
  );

  const fetchApplications = useCallback(
    async ({ page, pageSize, searchVal, status, dept } = {}) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("page", page || pagination.current);
        params.set("limit", pageSize || pagination.pageSize);
        if (searchVal ?? search) params.set("search", searchVal ?? search);
        if ((status ?? statusFilter) !== "all")
          params.set("status", status ?? statusFilter);
        if ((dept ?? deptFilter) !== "all")
          params.set("department", dept ?? deptFilter);

        const res = await fetch(`${HITECH_API}/admin/applications?${params}`, {
          headers: getAuthHeaders(),
        });

        if (res.status === 401) {
          localStorage.removeItem("hitech_admin_token");
          navigate("/admin", { replace: true });
          return;
        }

        const data = await res.json();
        if (data.success) {
          setApplications(data.applications);
          setPagination((prev) => ({
            ...prev,
            current: data.pagination.page,
            pageSize: data.pagination.limit,
            total: data.pagination.total,
          }));
          setCounts(data.counts);

          // Build department list from first load (unfiltered)
          if (
            !searchVal &&
            (status ?? statusFilter) === "all" &&
            (dept ?? deptFilter) === "all"
          ) {
            const depts = [
              ...new Set(
                data.applications
                  .map((a) => a.departmentAppliedFor)
                  .filter(Boolean),
              ),
            ];
            if (depts.length > 0)
              setDepartments((prev) => [...new Set([...prev, ...depts])]);
          }
        } else {
          notification.error({
            message: data.message || "Failed to load applications",
          });
        }
      } catch {
        notification.error({ message: "Network error" });
      } finally {
        setLoading(false);
      }
    },
    [
      pagination.current,
      pagination.pageSize,
      search,
      statusFilter,
      deptFilter,
      navigate,
    ],
  );

  // Initial load
  useEffect(() => {
    fetchApplications({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (value) => {
    setSearch(value);
    clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      setPagination((prev) => ({ ...prev, current: 1 }));
      fetchApplications({
        page: 1,
        searchVal: value,
        status: statusFilter,
        dept: deptFilter,
      });
    }, 400);
  };

  const handleStatusChange = (value) => {
    setStatusFilter(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchApplications({
      page: 1,
      searchVal: search,
      status: value,
      dept: deptFilter,
    });
  };

  const handleDeptChange = (value) => {
    setDeptFilter(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchApplications({
      page: 1,
      searchVal: search,
      status: statusFilter,
      dept: value,
    });
  };

  const handleTableChange = ({ current, pageSize }) => {
    setPagination((prev) => ({ ...prev, current, pageSize }));
    fetchApplications({
      page: current,
      pageSize,
      searchVal: search,
      status: statusFilter,
      dept: deptFilter,
    });
  };

  const handleUpdate = (updated) => {
    setApplications((prev) =>
      prev.map((a) => (a._id === updated._id ? updated : a)),
    );
    setSelectedApp(updated);
  };

  const logout = () => {
    localStorage.removeItem("hitech_admin_token");
    localStorage.removeItem("hitech_admin_user");
    navigate("/admin", { replace: true });
  };

  const columns = [
    {
      title: "#",
      width: 52,
      render: (_, __, i) =>
        (pagination.current - 1) * pagination.pageSize + i + 1,
    },
    {
      title: "Name",
      dataIndex: "name",
      render: (v) => v || "—",
    },
    {
      title: "Department",
      dataIndex: "departmentAppliedFor",
      render: (v) => v || "—",
    },
    {
      title: "Position",
      dataIndex: "positionAppliedFor",
      render: (v) => v || "—",
    },
    {
      title: "Contact",
      dataIndex: "contactNumber",
      render: (v) => v || "—",
    },
    {
      title: "Experience",
      dataIndex: "totalExperience",
      render: (v) => (v ? `${v} yrs` : "—"),
      width: 110,
    },
    {
      title: "Applied On",
      dataIndex: "createdAt",
      render: (v) => (v ? new Date(v).toLocaleDateString("en-IN") : "—"),
      width: 110,
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 110,
      render: (v) => (
        <Tag
          color={STATUS_COLORS[v || "pending"]}
          style={{ textTransform: "capitalize" }}
        >
          {v || "pending"}
        </Tag>
      ),
    },
    {
      title: "",
      width: 90,
      render: (_, record) => (
        <Tooltip title="View & Review">
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedApp(record);
            }}
            style={{ background: "#0d2e61", borderColor: "#0d2e61" }}
          >
            Review
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <img
            src="/Sources/images/logo/hitech_logo.png"
            alt="Hitech"
            style={{ height: 36, objectFit: "contain" }}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
          <span style={styles.headerTitle}>Admin Portal</span>
        </div>
        <Space>
          <Avatar
            icon={<UserOutlined />}
            style={{ background: "rgba(255,255,255,0.2)" }}
          />
          <span style={{ color: "rgba(255,255,255,0.85)", fontSize: 13 }}>
            {adminUser.name || adminUser.email || "Admin"}
          </span>
          <Button
            icon={<UserAddOutlined />}
            onClick={() => setCreateUserOpen(true)}
            type="text"
            style={{ color: "#fff", marginLeft: 8 }}
          >
            Add User
          </Button>
          <Button
            icon={<LogoutOutlined />}
            onClick={logout}
            type="text"
            style={{ color: "#fff" }}
          >
            Logout
          </Button>
        </Space>
      </div>

      <div style={styles.body}>
        {/* Stats */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          {[
            { label: "Total", value: counts.total, color: "#0d2e61" },
            { label: "Pending", value: counts.pending, color: "#fa8c16" },
            {
              label: "Shortlisted",
              value: counts.shortlisted,
              color: "#52c41a",
            },
            { label: "Rejected", value: counts.rejected, color: "#ff4d4f" },
          ].map((s) => (
            <Col xs={12} sm={6} key={s.label}>
              <Card style={styles.statCard}>
                <Statistic
                  title={s.label}
                  value={s.value}
                  valueStyle={{ color: s.color, fontWeight: 700 }}
                />
              </Card>
            </Col>
          ))}
        </Row>

        {/* Filters */}
        <div style={styles.filters}>
          <Input
            placeholder="Search by name, email, contact, position, department..."
            allowClear
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            prefix={<SearchOutlined />}
            style={{ maxWidth: 380 }}
          />
          <Space wrap>
            <Select
              value={statusFilter}
              onChange={handleStatusChange}
              style={{ width: 150 }}
            >
              <Select.Option value="all">All Status</Select.Option>
              <Select.Option value="pending">Pending</Select.Option>
              <Select.Option value="shortlisted">Shortlisted</Select.Option>
              <Select.Option value="rejected">Rejected</Select.Option>
            </Select>
            <Select
              value={deptFilter}
              onChange={handleDeptChange}
              style={{ width: 200 }}
            >
              <Select.Option value="all">All Departments</Select.Option>
              {departments.map((d) => (
                <Select.Option key={d} value={d}>
                  {d}
                </Select.Option>
              ))}
            </Select>
            <Tooltip title="Export to Excel">
              <Button
                icon={<FileExcelOutlined />}
                onClick={() => setExportOpen(true)}
                style={{ color: '#217346', borderColor: '#217346' }}
              >
                Export Excel
              </Button>
            </Tooltip>
            <Tooltip title="Refresh">
              <Button
                icon={<ReloadOutlined />}
                onClick={() =>
                  fetchApplications({
                    page: 1,
                    searchVal: search,
                    status: statusFilter,
                    dept: deptFilter,
                  })
                }
                loading={loading}
              />
            </Tooltip>
          </Space>
        </div>

        {/* Table */}
        <Card bodyStyle={{ padding: 0 }}>
          <Table
            dataSource={applications}
            columns={columns}
            rowKey="_id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: ["10", "15", "25", "50"],
              showTotal: (total, range) =>
                `${range[0]}–${range[1]} of ${total} applications`,
            }}
            onChange={(pag) => handleTableChange(pag)}
            onRow={(record) => ({
              onClick: () => setSelectedApp(record),
              style: { cursor: "pointer" },
            })}
            size="middle"
            scroll={{ x: 900 }}
          />
        </Card>
      </div>

      {selectedApp && (
        <ApplicationDetail
          application={selectedApp}
          onClose={() => setSelectedApp(null)}
          onUpdate={handleUpdate}
        />
      )}

      <CreateUser
        open={createUserOpen}
        onClose={() => setCreateUserOpen(false)}
        onCreated={() =>
          notification.success({ message: "User added to Hitech system" })
        }
      />

      <ExportDrawer
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        departments={departments}
      />
    </div>
  );
};

const styles = {
  page: { minHeight: "100vh", background: "#f0f2f5" },
  header: {
    background: "#0d2e61",
    padding: "0 24px",
    height: 60,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    position: "sticky",
    top: 0,
    zIndex: 100,
    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
  },
  headerLeft: { display: "flex", alignItems: "center", gap: 12 },
  headerTitle: { color: "#fff", fontSize: 18, fontWeight: 700 },
  body: { padding: "24px" },
  statCard: { borderRadius: 8, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
  filters: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 16,
  },
};

export default AdminDashboard;
